import { useEffect, useTransition } from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
  slugToStateName,
  stateNameToSlug,
  isValidStateSlug,
  STATE_CENTROIDS,
  CA_PROVINCE_CENTROIDS,
  PL_VOIVODESHIP_CENTROIDS,
} from '../utils/stateUtils';

interface GeoLocation {
  lat: number;
  lon: number;
  state?: string;
  country?: string;
}

interface UseBillUrlSyncParams {
  state: string | undefined;
  location: GeoLocation | null;
  setLocation: (location: GeoLocation | null) => void;
  billSlug: string | undefined;
  country: string;
  navigate: NavigateFunction;
}

/**
 * Keeps the URL state param and GeoLocation context in sync:
 * 1. When a user navigates directly to /bill/:country/:state/:slug, seeds the geo context.
 * 2. When the geo context changes (e.g. user picks a state), updates the URL.
 */
export function useBillUrlSync({
  state,
  location,
  setLocation,
  billSlug,
  country,
  navigate,
}: UseBillUrlSyncParams): void {
  const [, startTransition] = useTransition();

  // Sync URL state param → GeoLocation context (direct URL navigation)
  useEffect(() => {
    if (isValidStateSlug(state) && !location?.state) {
      const stateName = slugToStateName(state!);
      const centroid =
        STATE_CENTROIDS[stateName] || CA_PROVINCE_CENTROIDS[stateName] || PL_VOIVODESHIP_CENTROIDS[stateName];
      if (centroid) {
        setLocation({ lat: centroid.lat, lon: centroid.lon, state: stateName, country });
      }
    } else if (!state && location?.state) {
      // URL has no state param — user wants the 50-state grid; clear context
      setLocation(null);
    }
  }, [state, location?.state, setLocation, country]);

  // Sync GeoLocation context → URL (when location changes)
  useEffect(() => {
    if (!billSlug) return;
    // CRITICAL: If URL has no state param the user is viewing the 50-state grid.
    // Do NOT redirect them even if location context has a state.
    if (!state) return;

    if (location?.state) {
      const formattedState = stateNameToSlug(location.state);
      const locationCountry = location.country || 'united-states';
      // Only navigate if the state in URL differs from location context
      if (state !== formattedState) {
        startTransition(() => {
          navigate(`/bill/${locationCountry}/${formattedState}/${billSlug}`, { replace: true });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location.country excluded to prevent redirect loops
  }, [location?.state, billSlug, state, navigate, startTransition]);
}
